import { getBasicContentSchema } from '@/lib/zy-tiptap-utils';
import { Node } from '@tiptap/core';
import type { Node as PMNode, Schema } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import type { JSONContent } from '@tiptap/react';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { ZyITLabGoalNodeView } from './zyitlab-goal-node-view';
import { ZyITLabInstructionsNodeView } from './zyitlab-instructions-node-view';
import { ZyITLabModelSolutionNodeView } from './zyitlab-model-solution-node-view';
import ZyITLabNodeView from './zyitlab-node-view';
import { ZyITLabStepInstructionsNodeView, ZyITLabTaskInstructionsNodeView } from './zyitlab-rich-instructions-node-view';
import { DEFAULT_VM_IMAGE_NAME, DEFAULT_VM_INSTANCE_TYPE } from './zyitlab-shared';
import { ZyITLabStepNodeView } from './zyitlab-step-node-view';
import { ZyITLabTaskNodeView } from './zyitlab-task-node-view';
import { ZyITLabVmImageNodeView } from './zyitlab-vm-image-node-view';
import { ZyITLabVmImagesNodeView } from './zyitlab-vm-images-node-view';

export function defaultZyITLabVmImageContent(): JSONContent {
    return {
        type: 'zyITLabVmImage',
        attrs: {
            name: DEFAULT_VM_IMAGE_NAME,
            title: '',
            instanceType: DEFAULT_VM_INSTANCE_TYPE,
            startupCommands: '',
        },
    };
}

export function defaultZyITLabStepContent(): JSONContent {
    return {
        type: 'zyITLabStep',
        content: [
            {
                type: 'zyITLabStepInstructions',
                content: [{ type: 'paragraph' }],
            },
        ],
    };
}

export function defaultZyITLabTaskContent(): JSONContent {
    return {
        type: 'zyITLabTask',
        attrs: { taskDescription: '' },
        content: [
            {
                type: 'zyITLabTaskInstructions',
                content: [{ type: 'paragraph' }],
            },
            defaultZyITLabStepContent(),
        ],
    };
}

export function defaultZyITLabGoalContent(vmImageCount: number): JSONContent {
    const tests = Array.from({ length: vmImageCount }, (_, index) => ({
        type: 'zyITLabVmImageTest',
        attrs: {
            run: vmImageCount === 1 && index === 0,
            command: '',
            expected: '',
        },
    }));

    return {
        type: 'zyITLabGoal',
        attrs: { points: 1, message: '' },
        content: tests,
    };
}

export function defaultZyITLabDocumentContent(caption = ''): JSONContent {
    return {
        type: 'zyITLab',
        attrs: { caption },
        content: [
            {
                type: 'zyITLabInstructions',
                content: [{ type: 'paragraph' }],
            },
            {
                type: 'zyITLabModelSolution',
                content: [{ type: 'paragraph' }],
            },
            {
                type: 'zyITLabVmImages',
                content: [defaultZyITLabVmImageContent()],
            },
            defaultZyITLabTaskContent(),
        ],
    };
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        zyITLab: {
            insertZyITLab: (options?: { caption?: string }) => ReturnType;
            addZyITLabTask: (zyITLabPos: number | undefined) => ReturnType;
            addZyITLabOptionalTask: (zyITLabPos: number | undefined) => ReturnType;
            addZyITLabStep: (taskPos: number | undefined) => ReturnType;
            addZyITLabGoal: (stepPos: number | undefined) => ReturnType;
            addZyITLabVmImage: (vmImagesPos: number | undefined) => ReturnType;
        };
    }
}

function idAttribute() {
    return {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('id'),
        renderHTML: (attributes: Record<string, unknown>) => {
            if (!attributes.id) {
                return {};
            }
            return { id: attributes.id };
        },
    };
}

function nodeOfTypeAt(doc: PMNode, pos: number | undefined, typeName: string | string[]): PMNode | null {
    if (pos === undefined || pos < 0 || pos >= doc.content.size) {
        return null;
    }
    const node = doc.nodeAt(pos);
    if (!node) {
        return null;
    }
    const allowed = Array.isArray(typeName) ? typeName : [typeName];
    return allowed.includes(node.type.name) ? node : null;
}

function findChildIndex(parent: PMNode, childType: string): number {
    for (let i = 0; i < parent.childCount; i++) {
        if (parent.child(i).type.name === childType) {
            return i;
        }
    }
    return -1;
}

function countVmImages(zyITLabNode: PMNode): number {
    const vmImagesIndex = findChildIndex(zyITLabNode, 'zyITLabVmImages');
    if (vmImagesIndex < 0) {
        return 1;
    }
    return Math.max(1, zyITLabNode.child(vmImagesIndex).childCount);
}

function findZyITLabAncestor(doc: PMNode, fromPos: number): PMNode | null {
    const $pos = doc.resolve(fromPos);
    for (let depth = $pos.depth; depth > 0; depth--) {
        if ($pos.node(depth).type.name === 'zyITLab') {
            return $pos.node(depth);
        }
    }
    return null;
}

function findZyITLabAncestorPos(doc: PMNode, fromPos: number): { node: PMNode; pos: number } | null {
    const $pos = doc.resolve(fromPos);
    for (let depth = $pos.depth; depth > 0; depth--) {
        if ($pos.node(depth).type.name === 'zyITLab') {
            return { node: $pos.node(depth), pos: $pos.before(depth) };
        }
    }
    return null;
}

export const ZyITLabVmImageTestNodeHeadless = Node.create({
    name: 'zyITLabVmImageTest',

    group: 'zyITLabVmImageTest',

    atom: true,

    addAttributes() {
        return {
            id: idAttribute(),
            run: {
                default: false,
                parseHTML: element => element.getAttribute('run') === 'true',
            },
            command: {
                default: '',
                parseHTML: element => element.getAttribute('command') ?? '',
            },
            expected: {
                default: '',
                parseHTML: element => element.getAttribute('expected') ?? '',
            },
        };
    },

    parseHTML() {
        return [{ tag: 'zyITLabVmImageTest' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyITLabVmImageTest', HTMLAttributes];
    },
});

export const ZyITLabGoalNodeHeadless = Node.create({
    name: 'zyITLabGoal',

    content: 'zyITLabVmImageTest+',

    group: 'zyITLabGoal',

    defining: true,

    addAttributes() {
        return {
            id: idAttribute(),
            points: {
                default: 1,
                parseHTML: element => Number(element.getAttribute('points')) || 1,
            },
            message: { default: '' },
        };
    },

    parseHTML() {
        return [{ tag: 'zyITLabGoal' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyITLabGoal', HTMLAttributes, 0];
    },
});

export const ZyITLabStepInstructionsNodeHeadless = Node.create({
    name: 'zyITLabStepInstructions',

    content: function () {
        return getBasicContentSchema();
    },

    group: 'zyITLabStepInstructions',

    defining: true,

    addAttributes() {
        return {
            id: idAttribute(),
        };
    },

    parseHTML() {
        return [{ tag: 'zyITLabStepInstructions' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyITLabStepInstructions', HTMLAttributes, 0];
    },
});

export const ZyITLabTaskInstructionsNodeHeadless = Node.create({
    name: 'zyITLabTaskInstructions',

    content: function () {
        return getBasicContentSchema();
    },

    group: 'zyITLabTaskInstructions',

    defining: true,

    addAttributes() {
        return {
            id: idAttribute(),
        };
    },

    parseHTML() {
        return [{ tag: 'zyITLabTaskInstructions' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyITLabTaskInstructions', HTMLAttributes, 0];
    },
});

export const ZyITLabStepNodeHeadless = Node.create({
    name: 'zyITLabStep',

    content: 'zyITLabStepInstructions zyITLabGoal*',

    group: 'zyITLabStep',

    defining: true,

    isolating: true,

    addAttributes() {
        return {
            id: idAttribute(),
        };
    },

    parseHTML() {
        return [{ tag: 'zyITLabStep' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyITLabStep', HTMLAttributes, 0];
    },
});

export const ZyITLabTaskNodeHeadless = Node.create({
    name: 'zyITLabTask',

    content: 'zyITLabTaskInstructions zyITLabStep+',

    group: 'zyITLabTask',

    defining: true,

    isolating: true,

    addAttributes() {
        return {
            id: idAttribute(),
            taskDescription: { default: '' },
        };
    },

    parseHTML() {
        return [{ tag: 'zyITLabTask' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyITLabTask', HTMLAttributes, 0];
    },
});

export const ZyITLabVmImageNodeHeadless = Node.create({
    name: 'zyITLabVmImage',

    group: 'zyITLabVmImage',

    atom: true,

    addAttributes() {
        return {
            id: idAttribute(),
            name: { default: '' },
            title: { default: '' },
            instanceType: { default: 'small' },
            startupCommands: { default: '' },
        };
    },

    parseHTML() {
        return [{ tag: 'zyITLabVmImage' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyITLabVmImage', HTMLAttributes];
    },
});

export const ZyITLabVmImagesNodeHeadless = Node.create({
    name: 'zyITLabVmImages',

    content: 'zyITLabVmImage+',

    group: 'zyITLabSection',

    defining: true,

    addAttributes() {
        return {
            id: idAttribute(),
        };
    },

    parseHTML() {
        return [{ tag: 'zyITLabVmImages' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyITLabVmImages', HTMLAttributes, 0];
    },
});

export const ZyITLabModelSolutionNodeHeadless = Node.create({
    name: 'zyITLabModelSolution',

    content: function () {
        return getBasicContentSchema();
    },

    group: 'zyITLabSection',

    defining: true,

    addAttributes() {
        return {
            id: idAttribute(),
        };
    },

    parseHTML() {
        return [{ tag: 'zyITLabModelSolution' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyITLabModelSolution', HTMLAttributes, 0];
    },
});

export const ZyITLabInstructionsNodeHeadless = Node.create({
    name: 'zyITLabInstructions',

    content: function () {
        return getBasicContentSchema();
    },

    group: 'zyITLabSection',

    defining: true,

    addAttributes() {
        return {
            id: idAttribute(),
        };
    },

    parseHTML() {
        return [{ tag: 'zyITLabInstructions' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyITLabInstructions', HTMLAttributes, 0];
    },
});

export const ZyITLabNodeHeadless = Node.create({
    name: 'zyITLab',

    content: 'zyITLabInstructions zyITLabModelSolution zyITLabTask? zyITLabVmImages zyITLabTask+',

    group: 'block',

    draggable: true,

    isolating: true,

    addAttributes() {
        return {
            id: idAttribute(),
            caption: {
                default: '',
                parseHTML: (element: HTMLElement) => element.getAttribute('caption') || '',
                renderHTML: (attributes: Record<string, unknown>) => {
                    if (!attributes.caption) {
                        return {};
                    }
                    return { caption: attributes.caption };
                },
            },
        };
    },

    parseHTML() {
        return [{ tag: 'zyITLab' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyITLab', HTMLAttributes, 0];
    },

    addCommands() {
        return {
            insertZyITLab:
                (options = {}) =>
                ({ commands }) => {
                    return commands.insertContent(defaultZyITLabDocumentContent(options.caption ?? ''));
                },
            addZyITLabTask:
                (zyITLabPos: number | undefined) =>
                ({ chain, state }) => {
                    const zyITLabNode = nodeOfTypeAt(state.doc, zyITLabPos, 'zyITLab');
                    if (zyITLabPos === undefined || !zyITLabNode) {
                        return false;
                    }
                    const insertPos = zyITLabPos + zyITLabNode.nodeSize - 1;
                    return chain().focus().insertContentAt(insertPos, defaultZyITLabTaskContent()).run();
                },
            addZyITLabOptionalTask:
                (zyITLabPos: number | undefined) =>
                ({ chain, state }) => {
                    const zyITLabNode = nodeOfTypeAt(state.doc, zyITLabPos, 'zyITLab');
                    const modelSolutionIndex = zyITLabNode ? findChildIndex(zyITLabNode, 'zyITLabModelSolution') : -1;
                    if (zyITLabPos === undefined || !zyITLabNode || modelSolutionIndex < 0) {
                        return false;
                    }
                    const childAfterModelSolution =
                        modelSolutionIndex + 1 < zyITLabNode.childCount ? zyITLabNode.child(modelSolutionIndex + 1) : null;
                    if (childAfterModelSolution?.type.name === 'zyITLabTask') {
                        return false;
                    }
                    let insertPos = zyITLabPos + 1;
                    for (let i = 0; i <= modelSolutionIndex; i++) {
                        insertPos += zyITLabNode.child(i).nodeSize;
                    }
                    return chain().focus().insertContentAt(insertPos, defaultZyITLabTaskContent()).run();
                },
            addZyITLabStep:
                (taskPos: number | undefined) =>
                ({ chain, state }) => {
                    const taskNode = nodeOfTypeAt(state.doc, taskPos, 'zyITLabTask');
                    if (taskPos === undefined || !taskNode) {
                        return false;
                    }
                    const insertPos = taskPos + taskNode.nodeSize - 1;
                    return chain().focus().insertContentAt(insertPos, defaultZyITLabStepContent()).run();
                },
            addZyITLabGoal:
                (stepPos: number | undefined) =>
                ({ chain, state }) => {
                    const stepNode = nodeOfTypeAt(state.doc, stepPos, 'zyITLabStep');
                    if (stepPos === undefined || !stepNode) {
                        return false;
                    }
                    const zyITLab = findZyITLabAncestor(state.doc, stepPos);
                    const vmCount = zyITLab ? countVmImages(zyITLab) : 1;
                    const insertPos = stepPos + stepNode.nodeSize - 1;
                    return chain().focus().insertContentAt(insertPos, defaultZyITLabGoalContent(vmCount)).run();
                },
            addZyITLabVmImage:
                (vmImagesPos: number | undefined) =>
                ({ state, dispatch }) => {
                    const vmImagesNode = nodeOfTypeAt(state.doc, vmImagesPos, 'zyITLabVmImages');
                    if (vmImagesPos === undefined || !vmImagesNode) {
                        return false;
                    }

                    const insertPos = vmImagesPos + vmImagesNode.nodeSize - 1;
                    const vmImageNode = state.schema.nodeFromJSON(defaultZyITLabVmImageContent());
                    let tr = state.tr.insert(insertPos, vmImageNode);

                    const zyITLabInfo = findZyITLabAncestorPos(tr.doc, vmImagesPos);
                    if (zyITLabInfo) {
                        tr = appendGoalVmImageTestSync(tr, tr.doc, state.schema, zyITLabInfo.pos);
                    }

                    dispatch?.(tr);
                    return true;
                },
        };
    },
});

export function appendGoalVmImageTestSync(tr: Transaction, doc: PMNode, schema: Schema, zyITLabPos: number): Transaction {
    const freshZyITLab = doc.nodeAt(zyITLabPos);
    if (!freshZyITLab) {
        return tr;
    }

    const vmCount = countVmImages(freshZyITLab);
    const replacements: Array<{ from: number; to: number; node: PMNode }> = [];

    doc.nodesBetween(zyITLabPos, zyITLabPos + freshZyITLab.nodeSize, (node, pos) => {
        if (node.type.name !== 'zyITLabGoal') {
            return;
        }

        const currentTests = node.childCount;
        if (currentTests === vmCount) {
            return;
        }

        const tests = Array.from({ length: vmCount }, (_, index) => {
            const existing = index < currentTests ? node.child(index) : null;
            return schema.nodes.zyITLabVmImageTest.create({
                run: existing?.attrs.run ?? (vmCount === 1 && index === 0),
                command: existing?.attrs.command ?? '',
                expected: existing?.attrs.expected ?? '',
            });
        });

        replacements.push({
            from: pos,
            to: pos + node.nodeSize,
            node: node.type.create(node.attrs, tests),
        });
    });

    let nextTr = tr;
    for (let index = replacements.length - 1; index >= 0; index -= 1) {
        const replacement = replacements[index];
        nextTr = nextTr.replaceWith(replacement.from, replacement.to, replacement.node);
    }

    return nextTr;
}

export function syncGoalVmImageTests(
    editor: { state: { doc: PMNode; tr: Transaction; schema: Schema }; view: { dispatch: (tr: Transaction) => void } },
    _zyITLab: PMNode,
    zyITLabPos: number,
) {
    const tr = appendGoalVmImageTestSync(editor.state.tr, editor.state.doc, editor.state.schema, zyITLabPos);
    if (tr.docChanged) {
        editor.view.dispatch(tr);
    }
}

export const ZyITLabNode = ZyITLabNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyITLabNodeView);
    },
});

export const ZyITLabInstructionsNode = ZyITLabInstructionsNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyITLabInstructionsNodeView);
    },
});

export const ZyITLabModelSolutionNode = ZyITLabModelSolutionNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyITLabModelSolutionNodeView);
    },
});

export const ZyITLabVmImagesNode = ZyITLabVmImagesNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyITLabVmImagesNodeView);
    },
});

export const ZyITLabVmImageNode = ZyITLabVmImageNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyITLabVmImageNodeView);
    },
});

export const ZyITLabTaskNode = ZyITLabTaskNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyITLabTaskNodeView, { trackNodeViewPosition: true });
    },
});

export const ZyITLabStepNode = ZyITLabStepNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyITLabStepNodeView, { trackNodeViewPosition: true });
    },
});

export const ZyITLabTaskInstructionsNode = ZyITLabTaskInstructionsNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyITLabTaskInstructionsNodeView);
    },
});

export const ZyITLabStepInstructionsNode = ZyITLabStepInstructionsNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyITLabStepInstructionsNodeView);
    },
});

export const ZyITLabGoalNode = ZyITLabGoalNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyITLabGoalNodeView, { trackNodeViewPosition: true });
    },
});

export const ZyITLabVmImageTestNode = ZyITLabVmImageTestNodeHeadless;
